import React, { useEffect, useMemo, useRef, useState } from "react"
import styled, { useTheme } from "~/ui/styled"
import { observer } from "mobx-react-lite"
import LinearGradient from "~/ui/LinearGradient"
import {
  ColumnFlex,
  CenterFlex,
  BetweenFlex,
  StartFlex,
  Text,
} from "~components/El"
import { Avatar } from "~widgets/avatar"
import type { Message } from "~models/message"
import { AttachRedPacketData, RedpacketStatus, RedPacketType } from "~proto"
import { formatMessageTime } from "~utils"
import useGroupActions from "~hooks/useGroupActions"
import { HashHighlightText } from "~components/hash-highlight-text"
import { Linking } from "~/ui/primitives"
import {
  PopupRoot,
  PopupContentShell,
  PopupClose,
  PopupScrollArea,
  popupImageFillStyle,
} from "../components/PopupShell"

interface Props {
  message: Message
  redpacketData?: AttachRedPacketData
  onOpen: () => void
  onClose: () => void
}

const RedPacketDetailPopup: React.FC<Props> = ({ message, redpacketData, onClose }) => {
  const theme = useTheme()
  const { attach = {}, sender = {} } = message
  const redpacketInfo = attach?.redpacket_info

  const grapInfo = redpacketData?.grap_info || []
  const bestLuckUser = useMemo(() => {
    if (!grapInfo.length) return null
    return grapInfo.reduce((max, current) => {
      return Number(current.amount) > Number(max.amount) ? current : max
    })
  }, [grapInfo])
  const { handleRedPacketDetail } = useGroupActions()
  const requestedIdRef = useRef<string | null>(null)
  const fallbackBlockInfo = redpacketData ?? redpacketInfo
  const [apiBlockInfo, setApiBlockInfo] = useState<any>(null)
  const blockInfo = apiBlockInfo ?? fallbackBlockInfo

  const onSuccess = (data: any) => {
    if (data?.redpacket_data) {
      setApiBlockInfo(data.redpacket_data)
    }
  }
  const onError = () => {}
  useEffect(() => {
    const id = redpacketInfo?.redpacket_id
    if (!id || requestedIdRef.current === id) return
    requestedIdRef.current = id
    setApiBlockInfo(null)

    handleRedPacketDetail({ redpacket_id: id }, onSuccess, onError)
  }, [redpacketInfo?.redpacket_id, handleRedPacketDetail])

  const onVerifyBlock = () => {
    blockInfo?.verify_url && Linking.openURL(blockInfo?.verify_url);
  }
  return (
    <PopupRoot width="100%">
      <PopupContentShell height={475}>
        <LinearGradient
          colors={["#fb994d", "#ee4b49"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={popupImageFillStyle}
        >
          <ColumnFlex height="auto" width="100%" alignItems="center">
            <CenterFlex
              width="60px"
              height="60px"
              padding="2px"
              mt="26px"
              borderRadius="30px"
              bg={theme.colors.blockBackground}
            >
              <Avatar avatar={sender?.avatar} size={56} name={sender?.name} />
            </CenterFlex>
            <Text mt="8px" fontSize={18} color="#ffe6bd" fontWeight="600">
              {sender?.name || ""}发出的红包
            </Text>
            <Text mt="4px" fontSize={40} color="#ffff00" fontWeight="600">
              ¥{redpacketInfo?.total_amount || "0"}
            </Text>
            <Text mt="8px" fontSize={12} color="#ffe6bd">
              {redpacketData?.status === RedpacketStatus.EXPIRED ? "该红包已过期。" : ""}已领取
              {grapInfo.length}/{redpacketData.total_count}
            </Text>
          </ColumnFlex>
          {grapInfo.length > 0 && (
            <ColumnFlex height="auto" width="100%" padding="8px 10px 14px 10px" style={{ flex: 1 }}>
              <PopupScrollArea backgroundColor={theme.colors.blockBackground}>
                {blockInfo?.draw_block_number && (
                  <CenterFlex height="auto" padding="4px 4px">
                    <BlockInfo>
                      <StartFlex gap="4">
                        <Text fontSize={12} color="infoText">
                          开奖区块
                        </Text>
                        <Text color="#fff" fontSize={12}>
                          {blockInfo?.draw_block_number || ""}
                        </Text>
                        <Text onClick={onVerifyBlock} fontSize={12} color="buttonBackground">
                          验证开奖
                        </Text>
                      </StartFlex>
                      <StartFlex width="100%" gap="4">
                        <Text minWidth={'max-content'} fontSize={12} color="infoText">
                          取号哈希
                        </Text>
                        <HashHighlightText
                          text={blockInfo?.draw_text || ""}
                          color="#fff"
                          highlightColor="#ffff00"
                          fontSize={10}
                        />
                      </StartFlex>
                    </BlockInfo>
                  </CenterFlex>
                )}
                {grapInfo.map((item) => {
                  const isBest = item.receiver_id === bestLuckUser?.receiver_id
                  return (
                    <BetweenFlex height="auto" key={item.receiver_id} width="100%" padding="7px 10px">
                      <StartFlex height="auto" >
                        <Avatar avatar={item.avatar} size={32} name={item.receiver_nickname} />
                        <ColumnFlex width={'auto'} ml="8px">
                          <Text color={theme.colors.text} fontSize={14} fontWeight={600}>
                            {item.receiver_nickname}
                          </Text>
                          <Text color={theme.colors.secondaryText} fontSize={12}>
                            {formatMessageTime(item.grab_time)}
                          </Text>
                        </ColumnFlex>
                      </StartFlex>
                      <ColumnFlex width={'auto'} minWidth={'max-content'}>
                        <Text color={theme.colors.text} fontSize={14} fontWeight={600}>
                          ¥{item.amount}
                        </Text>
                        {redpacketData.redpacket_type === RedPacketType.LUCK &&
                          isBest &&
                          redpacketData.status === RedpacketStatus.FINISHED && (
                            <Text color="#ea580c" fontSize={12} mt="2px">
                              手气最佳
                            </Text>
                          )}
                      </ColumnFlex>
                    </BetweenFlex>
                  )
                })}
              </PopupScrollArea>
            </ColumnFlex>
          )}
        </LinearGradient>
      </PopupContentShell>
      <PopupClose onClose={onClose} />
    </PopupRoot>
  )
}

export default observer(RedPacketDetailPopup)

const BlockInfo = styled(ColumnFlex)`
  width: 100%;
  background-color: #353434;
  color: ${({ theme }) => theme.colors.text};
  border-radius: 8px;
  padding: 8px 4px;
  line-height: 22px;
`
